import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  DaashboardDataType,
  PelatihanPenyuluhDataType,
  PendidikanPenyuluhDataType,
  RiwayatJabatanPenyuluhDataType,
  WilayahBinaanPenyuluhDataType
} from './types'
import type {
  FilterDashboardValues,
  MutationPelatihanPenyuluhValues,
  MutationPendidikanPenyuluhValues,
  MutationRiwayatJabatanPenyuluhValues
} from './validation'

const QUERY_KEY = (key?: string | Record<string, any>) =>
  ['dashboard', key] as const

const getDetail = (): ServiceQueryOptionType<
  ServiceGetByIdResponse<DaashboardDataType>
> => ({
  queryKey: QUERY_KEY({ type: 'summaries' }),
  queryFn: async () => {
    try {
      const response =
        await api.get<ServiceGetByIdResponse<DaashboardDataType>>(`/dashboard`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllPendidikan = (
  params?: FilterDashboardValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PendidikanPenyuluhDataType>>
> => ({
  queryKey: QUERY_KEY({ ...params, type: 'pendidikan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PendidikanPenyuluhDataType>>
      >(
        `/ketenagaan/penyuluh/${params?.jenis_penyuluh}/${params?.id_penyuluh}/pendidikan`,
        {
          params: {
            ...params,
            id_penyuluh: undefined,
            jenis_penyuluh: undefined,
            tab: undefined
          }
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createPendidikan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PendidikanPenyuluhDataType>,
  MutationPendidikanPenyuluhValues
> => ({
  mutationFn: async (data: MutationPendidikanPenyuluhValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<PendidikanPenyuluhDataType>
      >(
        `/ketenagaan/penyuluh/${data?.jenis_penyuluh}/${data?.id_penyuluh}/pendidikan`,
        {
          ...data,
          jenjang_pendidikan: data.jenjang_pendidikan.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updatePendidikan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PendidikanPenyuluhDataType>,
  MutationPendidikanPenyuluhValues
> => ({
  mutationFn: async (data: MutationPendidikanPenyuluhValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PendidikanPenyuluhDataType>
      >(
        `/ketenagaan/penyuluh/${data?.jenis_penyuluh}/${data?.id_penyuluh}/pendidikan/${data.id_pendidikan}`,
        {
          ...data,
          jenjang_pendidikan: data.jenjang_pendidikan.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deletePendidikan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PendidikanPenyuluhDataType>,
  { id_penyuluh: string; id_data: string; jenis_penyuluh: string }
> => ({
  mutationFn: async (data: {
    id_penyuluh: string
    id_data: string
    jenis_penyuluh: string
  }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PendidikanPenyuluhDataType>
      >(
        `/ketenagaan/penyuluh/${data?.jenis_penyuluh}/${data?.id_penyuluh}/pendidikan/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

//START Pelatihan Swadaya
const getAllPelatihan = (
  params?: FilterDashboardValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PelatihanPenyuluhDataType>>
> => ({
  queryKey: QUERY_KEY({ ...params, type: 'pelatihan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PelatihanPenyuluhDataType>>
      >(
        `/ketenagaan/penyuluh/${params?.jenis_penyuluh}/${params?.id_penyuluh}/pelatihan`,
        {
          params: {
            ...params,
            id_penyuluh: undefined,
            jenis_penyuluh: undefined,
            tab: undefined
          }
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createPelatihan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PelatihanPenyuluhDataType>,
  MutationPelatihanPenyuluhValues
> => ({
  mutationFn: async (data: MutationPelatihanPenyuluhValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<PelatihanPenyuluhDataType>
      >(
        `/ketenagaan/penyuluh/${data?.jenis_penyuluh}/${data?.id_penyuluh}/pelatihan`,
        {
          ...data
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updatePelatihan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PelatihanPenyuluhDataType>,
  MutationPelatihanPenyuluhValues
> => ({
  mutationFn: async (data: MutationPelatihanPenyuluhValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PelatihanPenyuluhDataType>
      >(
        `/ketenagaan/penyuluh/${data?.jenis_penyuluh}/${data?.id_penyuluh}/pelatihan/${data.id_pelatihan}`,
        {
          ...data
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deletePelatihan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PelatihanPenyuluhDataType>,
  { id_penyuluh: string; id_data: string; jenis_penyuluh: string }
> => ({
  mutationFn: async (data: {
    id_penyuluh: string
    id_data: string
    jenis_penyuluh: string
  }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PelatihanPenyuluhDataType>
      >(
        `/ketenagaan/penyuluh/${data?.jenis_penyuluh}/${data?.id_penyuluh}/pelatihan/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

//START BIDANG KEAHLIAN PNS PPPK
const getAllRiwayatJabatan = (
  params?: FilterDashboardValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<RiwayatJabatanPenyuluhDataType>>
> => ({
  queryKey: QUERY_KEY({ ...params, type: 'pangkat-golongan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<RiwayatJabatanPenyuluhDataType>>
      >(
        `/ketenagaan/penyuluh/${params?.jenis_penyuluh}/${params?.id_penyuluh}/pangkat-golongan`,
        {
          params: {
            ...params,
            id_penyuluh: undefined,
            jenis_penyuluh: undefined,
            tab: undefined
          }
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createRiwayatJabatan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<RiwayatJabatanPenyuluhDataType>,
  MutationRiwayatJabatanPenyuluhValues
> => ({
  mutationFn: async (data: MutationRiwayatJabatanPenyuluhValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<RiwayatJabatanPenyuluhDataType>
      >(
        `/ketenagaan/penyuluh/${data?.jenis_penyuluh}/${data?.id_penyuluh}/pangkat-golongan`,
        {
          ...data,
          id_jabatan: data.id_jabatan.value,
          id_golongan: data.id_golongan.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updateRiwayatJabatan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<RiwayatJabatanPenyuluhDataType>,
  MutationRiwayatJabatanPenyuluhValues
> => ({
  mutationFn: async (data: MutationRiwayatJabatanPenyuluhValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<RiwayatJabatanPenyuluhDataType>
      >(
        `/ketenagaan/penyuluh/${data?.jenis_penyuluh}/${data?.id_penyuluh}/pangkat-golongan/${data.id_riwayat_jabatan}`,
        {
          ...data,
          id_jabatan: data.id_jabatan.value,
          id_golongan: data.id_golongan.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteRiwayatJabatan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<RiwayatJabatanPenyuluhDataType>,
  { id_penyuluh: string; id_data: string; jenis_penyuluh: string }
> => ({
  mutationFn: async (data: {
    id_penyuluh: string
    id_data: string
    jenis_penyuluh: string
  }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<RiwayatJabatanPenyuluhDataType>
      >(
        `/ketenagaan/penyuluh/${data?.jenis_penyuluh}/${data?.id_penyuluh}/pangkat-golongan/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllWilayahBinaan = (
  params?: FilterDashboardValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<WilayahBinaanPenyuluhDataType>>
> => ({
  queryKey: QUERY_KEY({ ...params, type: 'wilayah-kerja' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<WilayahBinaanPenyuluhDataType>>
      >(
        `/ketenagaan/penyuluh/${params?.jenis_penyuluh}/${params?.id_penyuluh}/wilayah-kerja`,
        {
          params: {
            ...params,
            id_penyuluh: undefined,
            jenis_penyuluh: undefined,
            tab: undefined
          }
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const dashboard = {
  queryKey: [QUERY_KEY(undefined)[0]],
  getDetail,
  getAllPendidikan,
  createPendidikan,
  updatePendidikan,
  deletePendidikan,

  getAllPelatihan,
  createPelatihan,
  updatePelatihan,
  deletePelatihan,

  getAllWilayahBinaan,

  getAllRiwayatJabatan,
  createRiwayatJabatan,
  updateRiwayatJabatan,
  deleteRiwayatJabatan
}
